// src/pages/AnimalList.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Typography,
  Stack,
  Paper,
  Alert,
  Autocomplete,
  TextField,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  IconButton,
  Snackbar,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Visibility,
  FilterList,
  ViewModule,
  TableRows,
  Search as SearchIcon,
  Pets,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import apiClient, { apiUrl } from "../../api/apiClient";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { useTheme, useMediaQuery } from "@mui/material";
import useAuth from "../../hooks/useAuth";
import { getTenantsList } from "../../api/tenant";
import { APP_HOME_PATH } from "../../config/routes";

const GENDER_OPTIONS = ["Male", "Female"];
const STATUS_OPTIONS = ["Healthy", "On Farm", "Sold", "Deceased", "Transferred"];
const CATEGORY_OPTIONS = ["Heifer", "Steer", "Bull", "Cow", "Calf"];

const ANIMAL_STATUS = {
  Healthy: {
    label: "Healthy",
    accent: "#2e7d32",
    chipBg: "#2e7d32",
    bgcolor: "#f1f8e9",
    borderColor: "#a5d6a7",
  },
  "On Farm": {
    label: "On Farm",
    accent: "#2e7d32",
    chipBg: "#2e7d32",
    bgcolor: "#f1f8e9",
    borderColor: "#a5d6a7",
  },
  Sold: {
    label: "Sold",
    accent: "#1565c0",
    chipBg: "#1565c0",
    bgcolor: "#e3f2fd",
    borderColor: "#90caf9",
  },
  Transferred: {
    label: "Transferred",
    accent: "#7b1fa2",
    chipBg: "#7b1fa2",
    bgcolor: "#f3e5f5",
    borderColor: "#ce93d8",
  },
  Deceased: {
    label: "Deceased",
    accent: "#757575",
    chipBg: "#757575",
    bgcolor: "#fafafa",
    borderColor: "#e0e0e0",
  },
  default: {
    label: "Unknown",
    accent: "#ed6c02",
    chipBg: "#ed6c02",
    bgcolor: "#fff8e1",
    borderColor: "#ffcc80",
  },
};

const ANIMAL_CHIP = {
  gender: { bgcolor: "#e3f2fd", color: "#1565c0", borderColor: "#90caf9" },
  category: { bgcolor: "#f3e5f5", color: "#7b1fa2", borderColor: "#ce93d8" },
  breed: { bgcolor: "#fff3e0", color: "#e65100", borderColor: "#ffcc80" },
};

function getAnimalStatusStyle(status) {
  return ANIMAL_STATUS[status] || {
    ...ANIMAL_STATUS.default,
    label: status || ANIMAL_STATUS.default.label,
  };
}

function AnimalMetaChip({ label, column }) {
  const style = ANIMAL_CHIP[column];
  return (
    <Chip
      size="small"
      label={label}
      component="span"
      clickable={false}
      sx={{
        height: 24,
        fontSize: "0.7rem",
        fontWeight: 600,
        bgcolor: style.bgcolor,
        color: style.color,
        border: "1px solid",
        borderColor: style.borderColor,
        pointerEvents: "none",
        cursor: "default",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

function AnimalStatusChip({ status }) {
  const style = getAnimalStatusStyle(status);
  return (
    <Chip
      size="small"
      label={style.label}
      component="span"
      clickable={false}
      sx={{
        height: 22,
        fontWeight: 700,
        fontSize: "0.7rem",
        flexShrink: 0,
        bgcolor: style.chipBg,
        color: "#fff",
        pointerEvents: "none",
        cursor: "default",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

function AnimalCardThumb({ filename, fill }) {
  const [broken, setBroken] = useState(false);
  const src = filename ? apiUrl(`uploads/${filename}`) : null;

  const placeholder = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: fill ? "100%" : undefined,
        minHeight: fill ? undefined : 140,
        color: "text.secondary",
        bgcolor: "action.hover",
      }}
    >
      <Pets sx={{ fontSize: fill ? 48 : 56, opacity: 0.45 }} />
    </Box>
  );

  if (!src || broken) return placeholder;

  return (
    <Box
      component="img"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      sizes="(max-width: 600px) 100vw, 280px"
      sx={{
        width: "100%",
        height: fill ? "100%" : undefined,
        minHeight: fill ? undefined : 140,
        objectFit: "cover",
        display: "block",
        bgcolor: "action.hover",
      }}
      onError={() => setBroken(true)}
    />
  );
}

function AnimalListCard({ row, profileQuery }) {
  const statusStyle = getAnimalStatusStyle(row.status);
  const isInactive = row.status === "Deceased" || row.status === "Sold";

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 2,
        border: "1px solid",
        borderColor: statusStyle.borderColor,
        bgcolor: statusStyle.bgcolor,
      }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "16 / 10",
          bgcolor: "action.hover",
          overflow: "hidden",
          opacity: isInactive ? 0.88 : 1,
        }}
      >
        <AnimalCardThumb filename={row.primary_image_filename} fill />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.28) 0%, transparent 55%)",
            pointerEvents: "none",
          }}
        />
      </Box>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          borderLeft: `4px solid ${statusStyle.accent}`,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {row.tag_number}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {row.alias?.trim() || "No alias"}
            </Typography>
          </Box>
          <AnimalStatusChip status={row.status} />
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={0.5}
          sx={{ mt: 1, rowGap: 0.5 }}
        >
          {row.gender ? (
            <AnimalMetaChip label={row.gender} column="gender" />
          ) : null}
          {row.category ? (
            <AnimalMetaChip label={row.category} column="category" />
          ) : null}
          {row.breed ? (
            <AnimalMetaChip label={row.breed} column="breed" />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: "auto", pt: 1.25 }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility />}
            component={RouterLink}
            to={`/animal/${row.id}${profileQuery}`}
            sx={{ fontWeight: 600 }}
          >
            View
          </Button>
          <IconButton
            size="small"
            color="primary"
            component={RouterLink}
            to={`/animal/${row.id}/edit${profileQuery}`}
            aria-label={`edit-animal-${row.id}`}
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: 1,
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
}

export default function AnimalList() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTableView = searchParams.get("view") === "table";
  const effectiveTableView = isTableView && !isSm;

  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState(null);

  const isSuperAdmin = user?.roles?.some(
    (r) => r === "super_admin" || r?.name === "super_admin"
  );

  const effectiveTenantId = isSuperAdmin
    ? tenantFilter?.id ?? null
    : user?.tenant_id ?? null;

  const farmTenantParam =
    isSuperAdmin && effectiveTenantId != null ? effectiveTenantId : null;

  const profileQuery =
    farmTenantParam != null ? `?tenant_id=${farmTenantParam}` : "";

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await getTenantsList({ for_filter: true });
        if (!cancelled) setTenants(Array.isArray(raw) ? raw : []);
      } catch {
        if (!cancelled) setTenants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin || tenants.length === 0 || tenantFilter != null) return;
    const raw = searchParams.get("tenant_id");
    if (!raw) return;
    const id = Number(raw);
    const t = tenants.find((x) => x.id === id);
    if (t) setTenantFilter(t);
  }, [isSuperAdmin, tenants, searchParams, tenantFilter]);

  const categoryFromUrl = searchParams.get("category");

  /** Deep link from dashboard: apply category filter once URL provides it */
  useEffect(() => {
    if (!categoryFromUrl) return;
    setFilterModel((fm) => {
      const has = fm.items?.some(
        (i) => i.field === "category" && i.value === categoryFromUrl
      );
      if (has) return fm;
      const items = [
        ...(fm.items || []).filter((i) => i.field !== "category"),
        {
          id: "url-category",
          field: "category",
          operator: "is",
          value: categoryFromUrl,
        },
      ];
      return { ...fm, items };
    });
  }, [categoryFromUrl]);

  const onOrgChange = (_, v) => {
    setTenantFilter(v);
    if (isSuperAdmin && v?.id != null) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("tenant_id", String(v.id));
          return p;
        },
        { replace: true }
      );
    }
  };

  const setViewSearchParams = useCallback(
    (nextTable) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (nextTable) p.set("view", "table");
          else p.delete("view");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "desc" }]);
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const [userColumnVisibility, setUserColumnVisibility] = useState({});

  const [cardSearchInput, setCardSearchInput] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const prevTableRef = useRef(isTableView);

  useEffect(() => {
    if (prevTableRef.current && !isTableView) {
      setCardSearchInput(
        (filterModel.quickFilterValues || []).join(" ").trim()
      );
    }
    prevTableRef.current = isTableView;
  }, [isTableView, filterModel.quickFilterValues]);

  useEffect(() => {
    if (isTableView) return;
    const t = setTimeout(() => {
      const tokens = cardSearchInput.trim().split(/\s+/).filter(Boolean);
      setFilterModel((fm) => {
        const prev = (fm.quickFilterValues || []).join(" ");
        const next = tokens.join(" ");
        if (prev === next) return fm;
        return { ...fm, quickFilterValues: tokens };
      });
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [cardSearchInput, isTableView]);

  const filterFieldValue = useCallback(
    (field) => {
      const it = (filterModel.items || []).find((i) => i.field === field);
      const raw = it?.value;
      if (raw == null || raw === "") return "";
      return Array.isArray(raw) ? raw[0] : raw;
    },
    [filterModel.items]
  );

  const setFilterField = useCallback((field, value) => {
    setFilterModel((fm) => {
      const items = (fm.items || []).filter((i) => i.field !== field);
      if (value) {
        items.push({
          id: `${field}-card-filter`,
          field,
          operator: "is",
          value,
        });
      }
      return { ...fm, items };
    });
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, []);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80, sortable: true },
      { field: "tag_number", headerName: "Tag Number", flex: 1, minWidth: 140 },
      { field: "alias", headerName: "Name / Alias", flex: 1, minWidth: 140 },
      { field: "breed", headerName: "Breed", flex: 1, minWidth: 140 },
      {
        field: "gender",
        headerName: "Gender",
        width: 120,
        filterable: true,
        type: "singleSelect",
        valueOptions: GENDER_OPTIONS,
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        filterable: true,
        type: "singleSelect",
        valueOptions: STATUS_OPTIONS,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value || "—"}
            color={params.value === "Healthy" ? "success" : "default"}
            variant="outlined"
          />
        ),
      },
      {
        field: "category",
        headerName: "Category",
        width: 140,
        filterable: true,
        type: "singleSelect",
        valueOptions: CATEGORY_OPTIONS,
      },
      {
        field: "kraal_name",
        headerName: "Kraal",
        flex: 1,
        minWidth: 140,
        valueGetter: (v, row) => row.kraal_name || "—",
      },
      {
        field: "dob",
        headerName: "DOB",
        width: 140,
        valueGetter: (v, row) =>
          row.dob ? new Date(row.dob).toISOString().slice(0, 10) : "—",
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        width: 140,
        renderCell: (params) => (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Visibility />}
              component={RouterLink}
              to={`/animal/${params.row.id}${profileQuery}`}
            >
              View
            </Button>{" "}
            &nbsp;
            <IconButton
              size="small"
              color="primary"
              component={RouterLink}
              to={`/animal/${params.row.id}/edit${profileQuery}`}
              aria-label="edit-animal"
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </>
        ),
      },
    ],
    [profileQuery]
  );

  const responsiveColumnOverlay = useMemo(() => {
    if (isXs) {
      return {
        pattern_markings: false,
        breed: false,
        kraal_name: false,
        category: false,
        status: true,
        alias: true,
        tag_number: true,
        dob: false,
      };
    }
    if (isSm) {
      return {
        pattern_markings: false,
        breed: true,
        kraal_name: false,
        category: true,
        status: true,
        alias: true,
        tag_number: true,
        dob: true,
      };
    }
    return {};
  }, [isXs, isSm]);

  const columnVisibilityModel = useMemo(
    () => ({ ...userColumnVisibility, ...responsiveColumnOverlay }),
    [userColumnVisibility, responsiveColumnOverlay]
  );

  const buildParams = useCallback(() => {
    const sort_by = sortModel?.[0]?.field ?? "id";
    const sort_dir = sortModel?.[0]?.sort ?? "desc";
    const qfTokens = (filterModel.quickFilterValues || [])
      .map((t) => String(t).trim())
      .filter(Boolean);
    const search = qfTokens.length ? qfTokens.join(" ") : undefined;

    let gender, status, category;
    (filterModel.items || []).forEach((it) => {
      const raw = it.value;
      const v =
        raw === 0 || raw === false
          ? raw
          : raw == null || raw === ""
            ? null
            : Array.isArray(raw)
              ? raw[0]
              : raw;
      if (v === null || v === undefined || v === "") return;
      if (it.field === "gender") gender = v;
      if (it.field === "status") status = v;
      if (it.field === "category") category = v;
    });

    const params = {
      page: paginationModel.page + 1,
      per_page: Number(paginationModel.pageSize),
      sort_by,
      sort_dir,
      search,
      gender,
      status,
      category,
    };
    if (farmTenantParam != null) {
      params.tenant_id = farmTenantParam;
    }
    return params;
  }, [paginationModel, sortModel, filterModel, farmTenantParam]);

  const fetchAnimals = useCallback(async () => {
    if (isSuperAdmin && effectiveTenantId == null) {
      setRows([]);
      setRowCount(0);
      setLoading(false);
      setError("");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const params = buildParams();
      const { data } = await apiClient.get("/animal/get_animals", { params });
      setRows(data.items || []);
      setRowCount(data.total || 0);
    } catch (err) {
      setError(err?.message || "Failed to fetch animals");
    } finally {
      setLoading(false);
    }
  }, [buildParams, isSuperAdmin, effectiveTenantId]);

  useEffect(() => {
    const t = setTimeout(fetchAnimals, 250);
    return () => clearTimeout(t);
  }, [fetchAnimals]);

  const sortControlValue = `${sortModel?.[0]?.field ?? "id"}-${sortModel?.[0]?.sort ?? "desc"}`;

  const handleCardSortChange = (e) => {
    const v = e.target.value;
    const [field, dir] = v.split("-");
    setSortModel([{ field, sort: dir }]);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const openFilterPopover = Boolean(filterAnchorEl);

  const cardToolbar = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
      flexWrap="wrap"
      sx={{ mb: 2 }}
      useFlexGap
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={effectiveTableView ? "table" : "cards"}
        onChange={(e, v) => {
          if (v == null) return;
          setViewSearchParams(v === "table");
        }}
        aria-label="animal list layout"
        sx={{ display: { xs: "none", md: "inline-flex" } }}
      >
        <ToggleButton value="cards" aria-label="card view">
          <ViewModule fontSize="small" sx={{ mr: 0.5 }} /> Cards
        </ToggleButton>
        <ToggleButton value="table" aria-label="table view">
          <TableRows fontSize="small" sx={{ mr: 0.5 }} /> Table
        </ToggleButton>
      </ToggleButtonGroup>

      <TextField
        size="small"
        placeholder="Search tag, alias…"
        value={cardSearchInput}
        onChange={(e) => setCardSearchInput(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 } }}
      />

      <Button
        size="small"
        variant={filterFieldValue("gender") || filterFieldValue("status") || filterFieldValue("category") ? "contained" : "outlined"}
        startIcon={<FilterList />}
        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
      >
        Filters
      </Button>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="animal-card-sort-label">Sort</InputLabel>
        <Select
          labelId="animal-card-sort-label"
          label="Sort"
          value={sortControlValue}
          onChange={handleCardSortChange}
        >
          <MenuItem value="id-desc">ID (newest)</MenuItem>
          <MenuItem value="id-asc">ID (oldest)</MenuItem>
          <MenuItem value="tag_number-asc">Tag number (A–Z)</MenuItem>
          <MenuItem value="tag_number-desc">Tag number (Z–A)</MenuItem>
          <MenuItem value="alias-asc">Alias (A–Z)</MenuItem>
          <MenuItem value="breed-asc">Breed (A–Z)</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  const animalCardsSection = (
    <>
      <Paper elevation={1} sx={{ width: "100%", p: 2 }}>
        {cardToolbar}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
              },
            }}
          >
            {rows.map((row) => (
              <AnimalListCard
                key={row.id}
                row={row}
                profileQuery={profileQuery}
              />
            ))}
          </Box>
        )}
        {!loading && rows.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No animals match your search or filters.
          </Typography>
        )}
        <TablePagination
          component="div"
          count={rowCount}
          page={paginationModel.page}
          onPageChange={(_, page) =>
            setPaginationModel((m) => ({ ...m, page }))
          }
          rowsPerPage={paginationModel.pageSize}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          onRowsPerPageChange={(e) => {
            setPaginationModel({
              page: 0,
              pageSize: parseInt(e.target.value, 10),
            });
          }}
        />
      </Paper>

      <Popover
        open={openFilterPopover}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack spacing={2} sx={{ p: 2, minWidth: 260 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="filt-gender">Gender</InputLabel>
            <Select
              labelId="filt-gender"
              label="Gender"
              value={filterFieldValue("gender") || ""}
              onChange={(e) =>
                setFilterField("gender", e.target.value || null)
              }
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {GENDER_OPTIONS.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="filt-status">Status</InputLabel>
            <Select
              labelId="filt-status"
              label="Status"
              value={filterFieldValue("status") || ""}
              onChange={(e) =>
                setFilterField("status", e.target.value || null)
              }
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="filt-category">Category</InputLabel>
            <Select
              labelId="filt-category"
              label="Category"
              value={filterFieldValue("category") || ""}
              onChange={(e) =>
                setFilterField("category", e.target.value || null)
              }
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {CATEGORY_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" onClick={() => setFilterAnchorEl(null)}>
            Close
          </Button>
        </Stack>
      </Popover>
    </>
  );

  const dataGridSection = (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Paper
        elevation={1}
        sx={{ width: "100%", minWidth: isXs ? 720 : "auto" }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{ px: 1, pt: 1, display: { xs: "none", md: "flex" } }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={effectiveTableView ? "table" : "cards"}
            onChange={(e, v) => {
              if (v == null) return;
              setViewSearchParams(v === "table");
            }}
          >
            <ToggleButton value="cards" aria-label="card view">
              <ViewModule fontSize="small" />
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <TableRows fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density={isSm ? "compact" : "standard"}
          rowHeight={56}
          showToolbar
          showQuickFilter
          slotProps={{
            toolbar: {
              csvOptions: { fileName: "animals" },
              printOptions: { disableToolbarButton: true },
            },
          }}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setPaginationModel((p) => ({ ...p, page: 0 }));
            setSortModel(model.length ? model : [{ field: "id", sort: "desc" }]);
          }}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={(model) => {
            setPaginationModel((p) => ({ ...p, page: 0 }));
            setFilterModel(model);
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model) =>
            setUserColumnVisibility(model)
          }
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "background.default",
            },
          }}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );

  if (isSuperAdmin && tenantFilter == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Typography color="text.primary">Animals</Typography>
        </Breadcrumbs>
        <Typography variant="h4" gutterBottom>
          Animal list
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization to load animals for that farm.
        </Alert>
        <Box sx={{ maxWidth: 480 }}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(o) => o?.name ?? ""}
            value={tenantFilter}
            onChange={onOrgChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organization"
                placeholder="Select organization"
                helperText="Platform admins browse animals per organization."
              />
            )}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
          />
        </Box>
      </Box>
    );
  }

  if (!isSuperAdmin && effectiveTenantId == null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Animal records are available once you belong to an organization.
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 2, flexWrap: "wrap" }}>
          <Link component={RouterLink} underline="hover" color="inherit" to={APP_HOME_PATH}>
            Dashboard
          </Link>
          <Typography color="text.primary">Animals</Typography>
        </Breadcrumbs>

        <Typography variant="h4">Animal List</Typography>

        {isSuperAdmin && (
          <Box sx={{ maxWidth: 480, my: 2 }}>
            <Autocomplete
              options={tenants}
              getOptionLabel={(o) => o?.name ?? ""}
              value={tenantFilter}
              onChange={onOrgChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization"
                  placeholder="Change organization"
                  helperText="Viewing animals for this farm."
                />
              )}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
            />
          </Box>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            color="success"
            component={RouterLink}
            to={
              farmTenantParam != null
                ? `/animal/create_animal?tenant_id=${farmTenantParam}`
                : "/animal/create_animal"
            }
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Add New Animal
          </Button>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={APP_HOME_PATH}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Back Home
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {effectiveTableView ? dataGridSection : animalCardsSection}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          variant="filled"
          elevation={3}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
